<template>
  <div class="bills-detail-box">
    <nav-index :title="'月结账单'"/>
    <div class="detail-content">
      <!-- 加载状态 -->
      <div v-if="detailLoading" class="loading-wrap">
        <van-loading size="30px" color="#ea6f7b"/>
      </div>

      <!-- 空状态 -->
      <div v-else-if="showEmpty">
        <div class="none-list">
          <img src="../../assets/images/none.svg" alt="">
          <div class="none">暂无账单数据</div>
        </div>
      </div>

      <!-- 账单内容 -->
      <div v-else class="detail-wrap">
        <!-- 账单标题 -->
        <div class="bill-title">{{ billLabelShort }}</div>

        <!-- 汇总卡片 -->
        <div class="summary-card">
          <!-- 期初余额 → 可点击跳转上一期 -->
          <div class="summary-row" style="cursor:pointer;" @click="goPrevBill">
            <span class="label">期初余额</span>
            <span class="value" style="display:flex;align-items:center;gap:8px;">
              {{ formatAmount(summary.beginReceivable - summary.beginPayable) }}
              <span v-if="hasPrevBill" style="font-size:12px;color:#999;font-weight:400;">查看上月 →</span>
            </span>
          </div>
          <div class="summary-row" v-if="summary.salesTotal > 0">
            <span class="label">销售单合计</span>
            <span class="value">{{ formatAmount(summary.salesTotal) }}</span>
          </div>
          <div class="summary-row" v-if="summary.clientReturnTotal > 0">
            <span class="label">退货单合计</span>
            <span class="value">-{{ formatAmount(summary.clientReturnTotal) }}</span>
          </div>
          <div class="summary-row" v-if="summary.receivableInc > 0 || summary.receivableDec > 0">
            <span class="label">费用调整</span>
            <span class="value">
              <span v-if="summary.receivableInc > 0">+{{ formatAmount(summary.receivableInc) }}</span>
              <span v-if="summary.receivableDec > 0" style="margin-left: 6px;">-{{ formatAmount(summary.receivableDec) }}</span>
            </span>
          </div>

          <!-- 已收款 → 可点击展开明细 -->
          <div class="summary-row" v-if="summary.receiptTotal > 0" style="cursor:pointer;" @click="toggleReceiptDetail">
            <span class="label">已收款</span>
            <span class="value" style="display:flex;align-items:center;gap:6px;">
              -{{ formatAmount(summary.receiptTotal) }}
              <span style="font-size:12px;color:#999;font-weight:400;">
                {{ showReceiptDetail ? '收起 ▲' : '展开 ▼' }}
              </span>
            </span>
          </div>

          <!-- 收款明细展开列表 -->
          <div v-if="showReceiptDetail && details.receipts && details.receipts.length > 0" class="receipt-detail-list">
            <div class="receipt-item" v-for="(item, idx) in details.receipts" :key="idx">
              <span class="receipt-no">{{ item.receiptNo }}</span>
              <span class="receipt-date">{{ formatDate(item.date) }}</span>
              <span class="receipt-amount">{{ formatAmount(item.amount) }}</span>
            </div>
          </div>

          <div class="summary-divider"></div>
          <div class="summary-row total">
            <span class="label">期末应结</span>
            <span class="value total-amount">{{ formatAmount(summary.netBalance) }}</span>
          </div>

          <!-- 供应商角色额外显示 -->
          <template v-if="role === 'both' || role === 'supplier'">
            <div class="summary-divider"></div>
            <div class="summary-row" style="font-size:13px;color:#999;">
              <span>—— 供应商角色 ——</span>
            </div>
            <div class="summary-row" v-if="summary.purchaseTotal > 0">
              <span class="label">进货单合计</span>
              <span class="value">+{{ formatAmount(summary.purchaseTotal) }}</span>
            </div>
            <div class="summary-row" v-if="summary.supplierReturnTotal > 0">
              <span class="label">退货单合计</span>
              <span class="value">-{{ formatAmount(summary.supplierReturnTotal) }}</span>
            </div>
            <div class="summary-row" v-if="summary.paymentTotal > 0">
              <span class="label">已付款</span>
              <span class="value paid">-{{ formatAmount(summary.paymentTotal) }}</span>
            </div>
          </template>
        </div>

        <!-- 白单明细（销售单） -->
        <div class="section" v-if="details.sales && details.sales.length > 0">
          <div class="section-title">
            <span class="dot blue"></span>
            白单明细（销售单）
            <span class="badge">{{ details.sales.length }} 笔</span>
          </div>
          <div class="order-item" v-for="(sale, idx) in details.sales" :key="idx">
            <div class="order-header">
              <span class="order-no">{{ sale.oid }}</span>
              <span class="order-date">{{ formatDate(sale.date) }}</span>
              <span class="order-amount">{{ formatAmount(sale.amount) }}</span>
            </div>
            <div class="order-items" v-if="sale.items && sale.items.length > 0">
              <div class="item-row" v-for="(item, i) in sale.items" :key="i">
                <span>{{ item.title }} x {{ item.quantity }}</span>
                <span>{{ formatAmount(item.subtotal) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 红单明细（退货单） -->
        <div class="section" v-if="details.clientReturns && details.clientReturns.length > 0">
          <div class="section-title">
            <span class="dot red"></span>
            红单明细（退货单）
            <span class="badge">{{ details.clientReturns.length }} 笔</span>
          </div>
          <div class="order-item return-item" v-for="(ret, idx) in details.clientReturns" :key="idx">
            <div class="order-header">
              <span class="order-no">{{ ret.returnNo }}</span>
              <span class="order-date">{{ formatDate(ret.date) }}</span>
              <span class="order-amount return">-{{ formatAmount(ret.amount) }}</span>
            </div>
            <div class="order-reason" v-if="ret.reason">原因：{{ ret.reason }}</div>
          </div>
        </div>

        <!-- 费用调整明细 -->
        <div class="section" v-if="details.adjustments && details.adjustments.length > 0">
          <div class="section-title">
            <span class="dot orange"></span>
            费用调整
            <span class="badge">{{ details.adjustments.length }} 笔</span>
          </div>
          <div class="order-item" v-for="(adj, idx) in details.adjustments" :key="idx">
            <div class="order-header">
              <span class="order-no">{{ adj.typeLabel }}</span>
              <span class="order-date">{{ formatDate(adj.date) }}</span>
              <span class="order-amount" :class="adj.amount > 0 ? 'positive' : 'negative'">
                {{ adj.amount > 0 ? '+' : '' }}{{ formatAmount(adj.amount) }}
              </span>
            </div>
            <div class="order-reason" v-if="adj.description">{{ adj.description }}</div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="bottom-actions" v-if="summary.netBalance > 0 && status !== 'paid'">
          <div class="pay-btn" @click="handlePay">
            <span>结清账单 {{ formatAmount(summary.netBalance) }}</span>
          </div>
        </div>
        <div class="bottom-actions" v-else-if="status === 'paid'">
          <div class="paid-tag">✅ 已结清</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavIndex from '../../components/nav/index.vue'
import { getMonthlyBillDetail, payMonthlyBill, getMonthlyBillList } from '@/api/api'
import { getopenIdByCode } from '@/utils/index'
import { Toast } from 'vant'

export default {
  name: 'BillsDetail',
  components: { NavIndex },
  data() {
    return {
      detailLoading: false,
      showEmpty: false,
      billLabel: '',
      billLabelShort: '',
      startDate: '',
      endDate: '',
      role: 'client',
      status: 'unpaid',
      showReceiptDetail: false,     // ← 新增：已收款展开/收起
      hasPrevBill: false,            // ← 新增：是否有上一期账单
      prevBillData: null,            // ← 新增：上一期账单数据
      summary: {
        beginReceivable: 0,
        beginPayable: 0,
        salesTotal: 0,
        clientReturnTotal: 0,
        receivableInc: 0,
        receivableDec: 0,
        receiptTotal: 0,
        purchaseTotal: 0,
        supplierReturnTotal: 0,
        paymentTotal: 0,
        netBalance: 0
      },
      details: {
        sales: [],
        clientReturns: [],
        adjustments: [],
        receipts: [],
        purchases: [],
        supplierReturns: [],
        payments: []
      },
      userInfo: localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo'))[0] : {},
      isPaying: false
    }
  },
  created() {
    getopenIdByCode()
  },
  mounted() {
    this.startDate = this.$route.query.startDate || ''
    this.endDate = this.$route.query.endDate || ''
    this.billLabel = this.$route.query.label || '账单详情'
    if (this.startDate && this.endDate) {
      this.formatBillLabel()
      this.getDetail()
      this.checkPrevBill()  // ← 新增：检查是否有上一期
    } else {
      Toast('参数错误')
      this.$router.back()
    }
  },
  methods: {
    getDetail() {
      const uid = this.userInfo?.uid
      if (!uid) {
        Toast('请先登录')
        return
      }
      this.detailLoading = true
      this.showEmpty = false
      getMonthlyBillDetail({
        uid,
        startDate: this.startDate,
        endDate: this.endDate
      })
        .then(res => {
          if (res.data.code === 200) {
            const data = res.data.data
            this.role = data.role || 'client'
            this.summary = data.summary || this.summary
            this.details = data.details || this.details
            // 判断状态
            if (this.summary.netBalance === 0) {
              this.status = 'paid'
            } else if (this.summary.receiptTotal > 0) {
              this.status = 'partial'
            } else {
              this.status = 'unpaid'
            }
            this.showEmpty = false
          } else {
            Toast(res.data.message || '加载失败')
            this.showEmpty = true
          }
        })
        .catch(() => {
          Toast('网络异常')
          this.showEmpty = true
        })
        .finally(() => {
          this.detailLoading = false
        })
    },

    // ============================================================
    // 新增：检查是否有上一期账单
    // ============================================================
    checkPrevBill() {
      const uid = this.userInfo?.uid
      if (!uid) return
      getMonthlyBillList({ uid }).then(res => {
        const list = res.data.data.list || []
        // 找到当前账单在列表中的位置
        const idx = list.findIndex(item => item.endDate === this.endDate)
        if (idx !== -1 && idx < list.length - 1) {
          this.hasPrevBill = true
          this.prevBillData = list[idx + 1]  // 上一期（索引+1）
        } else {
          this.hasPrevBill = false
        }
      }).catch(() => {
        this.hasPrevBill = false
      })
    },
    formatBillLabel() {
      const s = this.startDate || ''
      const e = this.endDate || ''
      if (s && e) {
        const sParts = s.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/)
        const eParts = e.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/)
        if (sParts && eParts) {
          this.billLabelShort = `${sParts[2]}-${sParts[3]} ${sParts[4]}:${sParts[5]} ~ ${eParts[2]}-${eParts[3]} ${eParts[4]}:${eParts[5]}`
        } else {
          this.billLabelShort = this.billLabel || '账单详情'
        }
      } else {
        this.billLabelShort = this.billLabel || '账单详情'
      }
    },
    // ============================================================
    // 新增：跳转到上一期账单
    // ============================================================
    goPrevBill() {
      if (!this.hasPrevBill || !this.prevBillData) {
        Toast('已是第一期账单')
        return
      }
      this.$router.push({
        path: '/bills-detail',
        query: {
          startDate: this.prevBillData.startDate || '',
          endDate: this.prevBillData.endDate || '',
          label: this.prevBillData.label || '上期账单'
        }
      })
      // 页面刷新重新加载
      this.$router.go(0)
    },

    // ============================================================
    // 新增：切换已收款展开/收起
    // ============================================================
    toggleReceiptDetail() {
      this.showReceiptDetail = !this.showReceiptDetail
    },

    handlePay() {
      const uid = this.userInfo?.uid
      if (!uid) {
        Toast('请先登录')
        return
      }
      const amount = this.summary.netBalance
      if (amount <= 0) {
        Toast('无需支付')
        return
      }
      this.isPaying = true
      payMonthlyBill({
        uid,
        startDate: this.startDate,
        endDate: this.endDate,
        amount: amount,
        openid: localStorage.getItem('openid') || ''
      })
        .then(res => {
          if (res.data.code === 200) {
            const receipt = res.data.data
            this.doWechatPay(receipt)
          } else {
            Toast(res.data.message || '支付失败')
            this.isPaying = false
          }
        })
        .catch(() => {
          Toast('网络异常')
          this.isPaying = false
        })
    },

    // TODO: 后续在这里真正调用微信支付
    // doWechatPay(_receipt) {
    //   Toast('调起微信支付...')
    //   this.isPaying = false
    // },

    formatAmount(val) {
      return Number(val || 0).toFixed(2)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return dateStr.replace('T', ' ').substring(0, 10)
    }
  }
}
</script>

<style scoped lang="scss">
.bills-detail-box {
  min-height: 100vh;
  background: #f5f5f5;
  padding-top: 50px;
}
.detail-content {
  padding: 10px 0 80px;
}
.loading-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
}
.bill-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  padding: 10px 16px 0;
  text-align: left;
  white-space: nowrap;          // ← 强制一行
  overflow: hidden;              // ← 超出隐藏
  text-overflow: ellipsis;       // ← 超出显示省略号
}
.summary-card {
  background: #fff;
  margin: 12px 16px;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 5px 0;
    font-size: 14px;
    color: #666;
    .value {
      font-weight: 500;
      &.paid { color: #52c41a; }
    }
    &.total {
      padding-top: 8px;
      .label { font-weight: 600; color: #333; }
      .total-amount { font-size: 20px; font-weight: 700; color: #ff4d4f; }
    }
  }
  .summary-divider {
    height: 1px;
    background: #f0f0f0;
    margin: 8px 0;
  }
}

// ============================================================
// 新增：收款明细列表样式
// ============================================================
.receipt-detail-list {
  background: #f7f7f7;
  border-radius: 6px;
  padding: 6px 12px;
  margin: 4px 0 8px 0;
  .receipt-item {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: #666;
    padding: 4px 0;
    border-bottom: 1px solid #eee;
    &:last-child { border-bottom: none; }
    .receipt-no { font-weight: 500; }
    .receipt-amount { font-weight: 500; color: #52c41a; }
  }
}

.section {
  background: #fff;
  margin: 12px 16px;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  .section-title {
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: 600;
    color: #333;
    padding-bottom: 10px;
    border-bottom: 1px solid #f0f0f0;
    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-right: 8px;
      &.blue { background: #1890ff; }
      &.red { background: #ff4d4f; }
      &.orange { background: #faad14; }
    }
    .badge {
      margin-left: 8px;
      font-size: 12px;
      color: #999;
      font-weight: 400;
    }
  }
}
.order-item {
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
  .order-header {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    .order-no { color: #333; font-weight: 500; }
    .order-date { color: #999; font-size: 12px; }
    .order-amount { font-weight: 600; color: #333; &.return { color: #ff4d4f; } &.positive { color: #52c41a; } &.negative { color: #ff4d4f; } }
  }
  .order-items {
    margin-top: 6px;
    padding-left: 4px;
    .item-row {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      color: #666;
      padding: 2px 0;
    }
  }
  .order-reason {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
  }
}
.bottom-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 750px;
  margin: 0 auto;
  padding: 12px 16px;
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.08);
  .pay-btn {
    background: #29323c;
    color: #fff;
    text-align: center;
    padding: 14px 0;
    border-radius: 30px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    &:active { opacity: 0.8; }
  }
  .paid-tag {
    text-align: center;
    color: #52c41a;
    font-size: 16px;
    padding: 10px 0;
  }
}
.none-list {
  margin: 60px auto 20px;
  width: 100%;
  max-width: 625px;
  .none {
    margin: 10px 0 0;
    color: #969699;
  }
  img {
    min-height: 142px;
  }
}
</style>